import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getMultiPropertySites, 
  getMultiPropertySiteById, 
  updateMultiPropertySite 
} from '@/api/multi-property';
import type { 
  MultiPropertySitesResponse, 
  MultiPropertySiteDetailResponse, 
  MultiPropertyUpdateResponse 
} from '@/api/multi-property/types';

/**
 * React Query hook for fetching all multi-property sites
 */
export function useMultiPropertySites(options?: { enabled?: boolean }) {
  return useQuery<MultiPropertySitesResponse, Error>({
    queryKey: ['multi-property-sites'],
    queryFn: async () => {
      try {
        const response = await getMultiPropertySites();
        return response;
      } catch (error) {
        console.error('Error in useMultiPropertySites hook:', error);
        throw error;
      }
    },
    enabled: options?.enabled ?? true, // Default to true if not specified
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Only retry network errors, not 4xx errors
      if (error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * React Query hook for fetching a specific multi-property site
 */
export function useMultiPropertySite(siteId: string) {
  return useQuery<MultiPropertySiteDetailResponse, Error>({
    queryKey: ['multi-property-sites', siteId],
    queryFn: async () => {
      try {
        return await getMultiPropertySiteById(siteId);
      } catch (error) {
        console.error('Error in useMultiPropertySite hook:', error);
        throw error;
      }
    },
    enabled: !!siteId, // Only run if siteId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * React Query mutation hook for updating a multi-property site
 */
export function useUpdateMultiPropertySite() {
  const queryClient = useQueryClient();
  
  return useMutation<MultiPropertyUpdateResponse, Error, { siteId: string; data: Record<string, any> }>({
    mutationFn: async ({ siteId, data }) => {
      return await updateMultiPropertySite(siteId, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch sites data
      queryClient.invalidateQueries({ queryKey: ['multi-property-sites'] });
      queryClient.invalidateQueries({ queryKey: ['multi-property-sites', variables.siteId] });
    },
    onError: (error) => {
      console.error('Error updating multi-property site:', error);
    },
  });
}